(() => {
  const panelLayout = (base, height, panelWidth = 155, overlap = 3) => {
    if (![base, height, panelWidth, overlap].every(Number.isFinite) || base <= 0 || height <= 0 || panelWidth <= 0 || overlap < 0 || overlap >= panelWidth) return null;
    if (Math.min(base, height) <= panelWidth) return { panels: 1, seams: 0, length: 0, axis: 'base' };
    const options = [{ span: base, run: height, axis: 'base' }, { span: height, run: base, axis: 'height' }].map(option => {
      const panels = 1 + Math.ceil((option.span - panelWidth) / (panelWidth - overlap));
      return { panels, seams: panels - 1, length: (panels - 1) * option.run / 100, axis: option.axis };
    });
    if (options.some(option => !Number.isSafeInteger(option.panels) || !Number.isFinite(option.length))) return null;
    return options.sort((a, b) => a.panels - b.panels || a.length - b.length)[0];
  };
  const calculate = (base, height, quantity, rate, finishes = {}) => {
    const { eyes = 0, reinforcement = false, eyeletCents = 50, reinforcementCents = 150, jointCents = 150, panelWidth = 155, overlap = 3 } = finishes;
    if (![base, height, quantity, rate, eyes, eyeletCents, reinforcementCents, jointCents].every(Number.isFinite) || base < 1 || height < 1 || !Number.isInteger(quantity) || quantity < 1 || rate <= 0 || !Number.isInteger(eyes) || eyes < 0 || !Number.isInteger(eyeletCents) || eyeletCents < 0 || !Number.isInteger(reinforcementCents) || reinforcementCents < 0 || !Number.isInteger(jointCents) || jointCents < 0 || overlap < 3) return null;
    const layout = panelLayout(base, height, panelWidth, overlap);
    if (!layout) return null;
    const area = base * height / 10000;
    const billablePerPiece = Math.max(1, area);
    const perimeterPerPiece = 2 * (base + height) / 100;
    const basePrintNetCents = Math.round(billablePerPiece * rate * 100) * quantity;
    const jointNetCents = Math.round(layout.length * jointCents) * quantity;
    const printNetCents = basePrintNetCents + jointNetCents;
    const eyeletsNetCents = eyes * eyeletCents * quantity;
    const reinforcementNetCents = reinforcement ? Math.round(perimeterPerPiece * reinforcementCents) * quantity : 0;
    const netCents = printNetCents + eyeletsNetCents + reinforcementNetCents;
    const grossCents = Math.round(netCents / quantity * 1.22) * quantity;
    if (![netCents, grossCents, basePrintNetCents, jointNetCents, printNetCents, eyeletsNetCents, reinforcementNetCents].every(Number.isSafeInteger)) return null;
    return { area, billablePerPiece, billableTotal: billablePerPiece * quantity, perimeter: perimeterPerPiece * quantity, joint: layout.seams > 0, layout, basePrintNetCents, jointNetCents, printNetCents, eyeletsNetCents, reinforcementNetCents, netCents, grossCents };
  };
  const recommendedEyelets = (base, height, arrangement) => {
    if (![base, height].every(Number.isFinite) || base <= 0 || height <= 0) return null;
    if (arrangement === 'horizontal') return 2 * (Math.ceil(base / 50) + 1);
    if (arrangement === 'vertical') return 2 * (Math.ceil(height / 50) + 1);
    return 2 * Math.ceil(base / 50) + 2 * Math.ceil(height / 50);
  };
  const eyeletPoints = (width, height, count, arrangement) => {
    const n = Math.min(80, count);
    const line = (amount, from, to) => Array.from({ length: amount }, (_, i) => {
      const t = amount === 1 ? .5 : i / (amount - 1);
      return [from[0] + (to[0] - from[0]) * t, from[1] + (to[1] - from[1]) * t];
    });
    if (arrangement === 'horizontal') return [...line(Math.ceil(n / 2), [0, 0], [width, 0]), ...line(Math.floor(n / 2), [0, height], [width, height])];
    if (arrangement === 'vertical') return [...line(Math.ceil(n / 2), [0, 0], [0, height]), ...line(Math.floor(n / 2), [width, 0], [width, height])];
    const corners = [[0, 0], [width, 0], [width, height], [0, height]];
    if (n <= 4) return corners.slice(0, n);
    const lengths = [width, height, width, height];
    const exact = lengths.map(v => (n - 4) * v / (2 * (width + height)));
    const amounts = exact.map(Math.floor);
    const order = exact.map((v, i) => ({ i, fraction: v - amounts[i] })).sort((a, b) => b.fraction - a.fraction);
    const missing = n - 4 - amounts.reduce((a, b) => a + b, 0);
    for (let i = 0; i < missing; i++) amounts[order[i].i]++;
    return [...corners, ...amounts.flatMap((amount, side) => line(amount + 2, corners[side], corners[(side + 1) % 4]).slice(1, -1))];
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = { calculate, panelLayout, eyeletPoints, recommendedEyelets };
  if (typeof customElements === 'undefined' || customElements.get('tsp-banner-mesh')) return;
  customElements.define('tsp-banner-mesh', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.labels = JSON.parse(this.querySelector('[data-labels]').textContent);
      this.form = this.querySelector('form');
      this.money = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });
      this.number = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 4 });
      this.addButton = this.form.querySelector('[name="add"]');
      this.engine = this.querySelector('[data-live-options]');
      this.status = this.querySelector('[data-engine-status]');
      this.form.addEventListener('submit', event => {
        this.verifyEngine();
        if (!this.checkoutReady) {
          event.preventDefault(); event.stopImmediatePropagation();
          this.status.textContent = this.labels.price_check;
        }
      }, { capture: true, signal: this.controller.signal });
      window.addEventListener('options:rendered', () => this.syncEngine(), { signal: this.controller.signal });
      window.addEventListener('options:price', () => requestAnimationFrame(() => this.verifyEngine()), { signal: this.controller.signal });
      this.engineObserver = new MutationObserver(() => {
        cancelAnimationFrame(this.engineFrame);
        this.engineFrame = requestAnimationFrame(() => { this.syncEngine(); this.verifyEngine(); });
      });
      this.engineObserver.observe(this.querySelector('.tsp-lf__engine'), { childList: true, subtree: true, characterData: true });
      this.addEventListener('input', event => {
        if (!event.target.matches('[data-input]')) return;
        if (event.target.matches('[data-input="eyes"]')) this.manualEyes = true;
        this.update();
      }, { signal: this.controller.signal });
      this.querySelector('[data-use-recommendation]').addEventListener('click', () => { this.manualEyes = false; this.update(); }, { signal: this.controller.signal });
      this.addEventListener('change', event => { if (event.target.matches('[data-input]')) this.update(); }, { signal: this.controller.signal });
      this.update();
    }
    disconnectedCallback() { this.controller?.abort(); this.engineObserver?.disconnect(); cancelAnimationFrame(this.engineFrame); this.controller = null; }
    engineFields() {
      return {
        dimensions: [...this.engine.querySelectorAll('[data-option="_dimensioni"] input')].filter(e => e.type !== 'hidden'),
        reinforcement: this.engine.querySelector('[data-option="_rinforzo"] input[type="checkbox"]'),
        eyelets: this.engine.querySelector('[data-option="_occhielli"] input[type="checkbox"]'),
        count: this.engine.querySelector('[data-option="_numero_occhielli"] input:not([type="hidden"])')
      };
    }
    syncEngine() {
      if (!this.result || this.syncing) return;
      this.syncing = true;
      try {
        const f = this.engineFields();
        const input = (el, value) => {
          if (!el || el.value === String(value)) return;
          el.value = String(value);
          el.dispatchEvent(new Event('input', { bubbles: true }));
          el.dispatchEvent(new Event('change', { bubbles: true }));
        };
        const check = (el, value) => {
          if (!el || el.checked === value) return;
          el.checked = value;
          el.dispatchEvent(new Event('input', { bubbles: true }));
          el.dispatchEvent(new Event('change', { bubbles: true }));
        };
        input(f.dimensions[0], this.read('base'));
        input(f.dimensions[1], this.read('height'));
        check(f.reinforcement, this.querySelector('[data-input="reinforcement"]').checked);
        check(f.eyelets, this.querySelector('[data-input="eyelets-enabled"]').checked);
        input(this.engineFields().count, this.read('eyes'));
      } finally { this.syncing = false; }
      this.verifyEngine();
    }
    verifyEngine() {
      const f = this.engineFields();
      const raw = this.querySelector('[data-engine-price]').textContent.trim();
      const normalized = raw.replace(/[^0-9,.-]/g, '').replace(/\.(?=\d{3}(?:\D|$))/g, '').replace(',', '.');
      const quoted = Math.round(Number(normalized) * 100);
      const eyes = this.querySelector('[data-input="eyelets-enabled"]').checked;
      const same = this.result && f.dimensions.length === 2 &&
        Number(f.dimensions[0].value) === this.read('base') && Number(f.dimensions[1].value) === this.read('height') &&
        f.reinforcement?.checked === this.querySelector('[data-input="reinforcement"]').checked &&
        f.eyelets?.checked === eyes && (!eyes || Number(f.count?.value) === this.read('eyes'));
      this.checkoutReady = Boolean(same && raw && quoted === this.result.grossCents / this.read('quantity'));
      this.addButton.disabled = !this.checkoutReady;
      this.status.textContent = this.checkoutReady ? this.labels.ready : this.labels.loading;
      this.dataset.checkoutReady = String(this.checkoutReady);
      this.dataset.quotedCents = String(quoted);
    }
    read(name) {
      const input = this.querySelector(`[data-input="${name}"]`);
      const raw = input.value.trim();
      if (!/^\d+(?:[.,]\d+)?$/.test(raw)) return NaN;
      return Number(raw.replace(',', '.'));
    }
    set(name, value) { this.querySelector(`[data-output="${name}"]`).textContent = value; }
    property(name, value) { this.querySelector(`[data-property="${name}"]`).value = value; }
    update() {
      const base = this.read('base');
      const height = this.read('height');
      const quantity = this.read('quantity');
      const invalid = this.querySelector('[data-invalid]');
      const summary = this.querySelector('[data-summary]');
      const svg = this.querySelector('[data-format-diagram]');
      const enabled = this.querySelector('[data-input="eyelets-enabled"]').checked;
      const suggested = recommendedEyelets(base, height, this.querySelector('[data-input="arrangement"]:checked').value);
      this.querySelector('[data-use-recommendation]').disabled = suggested === null;
      this.set('recommended_count', suggested === null ? '—' : String(suggested));
      if (!this.manualEyes && suggested !== null) this.querySelector('[data-input="eyes"]').value = String(suggested);
      const eyes = enabled ? this.read('eyes') : 0;
      this.querySelector('[data-eyelet-options]').hidden = !enabled;
      this.querySelector('[data-input="eyes"]').disabled = !enabled;
      this.querySelectorAll('[data-input="arrangement"]').forEach(input => { input.disabled = !enabled; });
      const reinforcement = this.querySelector('[data-input="reinforcement"]').checked;
      const result = calculate(base, height, quantity, Number(this.dataset.netRate), { eyes, reinforcement, eyeletCents: Number(this.dataset.eyeletCents), reinforcementCents: Number(this.dataset.reinforcementCents), jointCents: Number(this.dataset.jointCents), panelWidth: Number(this.dataset.panelWidth), overlap: Number(this.dataset.overlap) });
      const eyesValid = Number.isInteger(eyes) && eyes >= 0;
      this.result = result;
      invalid.hidden = Boolean(result) && eyesValid;
      summary.hidden = !result || !eyesValid;
      svg.hidden = !result;
      if (!result || !eyesValid) {
        ['base','height','copies','finish'].forEach(name => this.property(name, ''));
        this.checkoutReady = false; this.addButton.disabled = true;
        return;
      }
      this.set('eyelets_fee', this.money.format(result.eyeletsNetCents / 100));
      this.set('reinforcement_fee', this.money.format(result.reinforcementNetCents / 100));
      this.set('net', this.money.format(result.netCents / 100));
      this.set('gross', this.money.format(result.grossCents / 100));
      this.set('production', result.joint ? this.labels.joint : this.labels.single);
      this.querySelector('[data-joint-note]').hidden = !result.joint;
      this.querySelector('[data-minimum-note]').hidden = result.area >= 1;
      const selectedArrangement = this.querySelector('[data-input="arrangement"]:checked');
      const arrangement = selectedArrangement.value;
      const finish = `${this.labels.reinforcement}: ${reinforcement ? this.labels.yes : this.labels.no}; ${this.labels.eyes}: ${eyes}; ${eyes > 0 ? selectedArrangement.dataset.label : this.labels.without_eyes}`;
      this.property('base', `${this.number.format(base)} cm`);
      this.property('height', `${this.number.format(height)} cm`);
      this.property('copies', String(quantity));
      this.form.querySelector('[name="quantity"]').value = String(quantity);
      this.syncEngine();
      this.property('finish', finish);
      this.set('visual_finish', `${reinforcement ? this.labels.reinforcement : this.labels.plain} · ${eyes > 0 ? `${eyes} ${this.labels.eyelets_short.toLowerCase()} · ${selectedArrangement.dataset.label.toLowerCase()}` : this.labels.without_eyes}`);
      const ratio = base / height;
      const w = Math.min(400, 180 * ratio);
      const h = Math.min(180, 400 / ratio);
      const x = 250 - w / 2;
      const y = 130 - h / 2;
      const rect = this.querySelector('[data-diagram-rect]');
      rect.setAttribute('x', x); rect.setAttribute('y', y);
      rect.setAttribute('width', w); rect.setAttribute('height', h);
      const border = this.querySelector('[data-diagram-border]');
      border.setAttribute('x', x + 5); border.setAttribute('y', y + 5);
      border.setAttribute('width', Math.max(0, w - 10)); border.setAttribute('height', Math.max(0, h - 10));
      border.style.display = reinforcement && w > 12 && h > 12 ? '' : 'none';
      const art = this.querySelector('[data-diagram-art]');
      const scale = Math.min(1, w / 300, h / 80);
      art.setAttribute('transform', `translate(250 130) scale(${scale}) translate(-250 -130)`);
      const holes = this.querySelector('[data-diagram-eyes]');
      const inset = Math.min(8, w / 5, h / 5);
      const nodes = eyeletPoints(w - 2 * inset, h - 2 * inset, eyes, arrangement).map(([cx, cy]) => {
        const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        circle.setAttribute('cx', x + inset + cx); circle.setAttribute('cy', y + inset + cy);
        circle.setAttribute('r', Math.min(3.5, inset / 2)); circle.setAttribute('fill', '#fff');
        circle.setAttribute('stroke', '#555'); circle.setAttribute('stroke-width', '1.5');
        return circle;
      });
      holes.replaceChildren(...nodes);
      const seams = Array.from({ length: Math.min(40, result.layout.seams) }, (_, i) => {
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        const t = (i + 1) / result.layout.panels;
        const vertical = result.layout.axis === 'base';
        line.setAttribute('x1', vertical ? x + w * t : x);
        line.setAttribute('x2', vertical ? x + w * t : x + w);
        line.setAttribute('y1', vertical ? y : y + h * t);
        line.setAttribute('y2', vertical ? y + h : y + h * t);
        return line;
      });
      this.querySelector('[data-diagram-seams]').replaceChildren(...seams);
      const bx = this.querySelector('[data-diagram-base]');
      bx.setAttribute('y', y + h + 26); bx.textContent = `${this.number.format(base)} cm`;
      const hy = this.querySelector('[data-diagram-height]');
      hy.setAttribute('x', 250); hy.setAttribute('y', y - 14); hy.textContent = `${this.number.format(height)} cm`;
    }
  });
})();

